/**
 * Copyright IBM Corp. 2022, 2026
 */

import { forwardRef } from 'react'
import { Card as MDSCard } from '../../components/card'
import { Text } from '../../components/text'
import { FlightIcon } from '../../components/flight-icon'
import { ProductBadge } from '../product-badge'
import { Thumbnail, PersonThumbnail, LogoThumbnail } from './thumbnails'
import type {
	CardPrimitiveProps,
	MetaProps,
	ContentProps,
	HeadingProps,
	ProductBadgesProps,
	DescriptionProps,
} from './types'
import s from './style.module.css'
import classNames from 'classnames'

const Root = forwardRef<HTMLDivElement, CardPrimitiveProps>((props, ref) => {
	const {
		withArrow = true,
		link,
		clickHandler,
		isExternal,
		children,
		className,
		'aria-label': ariaLabel,
	} = props

	return (
		<MDSCard
			className={classNames(s.card, className)}
			href={link}
			onClickCapture={clickHandler}
			isExternal={isExternal}
			ref={ref}
			aria-label={ariaLabel}
		>
			{children}
			<div className={s.cta}>
				{withArrow ? <FlightIcon name="arrow-right" size={24} /> : null}
			</div>
		</MDSCard>
	)
})

Root.displayName = 'CardPrimitives.Root'

const Meta = ({ items }: MetaProps) => {
	return (
		<div className={s.meta} data-testid="wpl-card-meta">
			<Text.Label>
				{items.map((item, stableIdx) => {
					const isLastItem = stableIdx === items.length - 1
					return (
						<>
							{item}
							{!isLastItem ? (
								<span className={s.metaSeparator} aria-hidden={true}>
									|
								</span>
							) : null}
						</>
					)
				})}
			</Text.Label>
		</div>
	)
}

Meta.displayName = 'Meta'

const Content = ({ children }: ContentProps) => {
	return (
		<div className={s.content} data-testid="wpl-card-content">
			{children}
		</div>
	)
}

Content.displayName = 'Content'

const Heading = ({ as = 'h2', children }: HeadingProps) => {
	return (
		<Text.DisplayExpressive tag={as} size="200" className={s.heading}>
			{children}
		</Text.DisplayExpressive>
	)
}

Heading.displayName = 'Heading'

const ProductBadges = ({ badges, children }: ProductBadgesProps) => {
	return (
		<div className={s.productBadges} data-testid="wpl-card-badges">
			{badges.map((badge, stableIdx) => {
				return (
					<ProductBadge
						// eslint-disable-next-line react/no-array-index-key
						key={`product-badge-${stableIdx}`}
						productName={badge}
						hasDot={true}
					/>
				)
			})}
			{children}
		</div>
	)
}

ProductBadges.displayName = 'ProductBadges'

const Description = ({ children }: DescriptionProps) => {
	return (
		<Text.Body tag="p" size="200" className={s.description}>
			{children}
		</Text.Body>
	)
}

Description.displayName = 'Description'

const CardPrimitives = {
	Root,
	Thumbnail,
	PersonThumbnail,
	LogoThumbnail,
	Meta,
	Content,
	Heading,
	Description,
	ProductBadges,
}

export { CardPrimitives }
